import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import { Drawer, IconButton, Stack, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import Button from "components/basic/button";
import ScrollContainer from "components/basic/scrollContainer";
import Typography from "components/basic/typography";
import TextArea from "components/basic/textArea";
import AutoComplete from "components/basic/autoComplete";
import LoadingSpinner from "components/basic/loadingSpinner";
import { useSnack } from "hooks/useSnack";
import { useMeQuery } from "generated/graphql";

import { BookingType } from "pages/dashboard/pages/calendar/eventTypes";
import {
  Slot,
  earlierDate,
  horizonMaxDate,
  earliestBookableDate,
} from "pages/dashboard/pages/meetings/helpers/availabilitySlots";
import {
  CUSTOMER_REQUEST_TYPES,
  CUSTOMER_TYPE_LABELS,
  quoteMaxDate,
  useQuoteVendorAvailability,
  useRequestBooking,
} from "pages/dashboard/pages/calendar/booking/components/BookAppointmentModal/bookAppointmentApi";
import { useBookingResponse, useQuoteBooking } from "./bookVisitApi";
import ScheduleFields from "./components/ScheduleFields";
import BookingConfirmation from "./components/BookingConfirmation";

// The MUI DateCalendar's natural width — the content column matches it so the
// panel is exactly as wide as its content.
const CONTENT_WIDTH = "min(320px, 86vw)";

interface BookVisitDrawerProps {
  // The just-created (approved) quote the appointment is booked against.
  quoteId: number;
  vendorName: string;
  // Epoch ms. Absent for a quote created moments ago, before the account's
  // quote list has refetched — the server enforces the same cap regardless.
  quoteExpiresAt?: string | null;
  onClose: () => void;
}

/**
 * Right-side panel opened from the estimate's "Book a Call" / "Reschedule"
 * action. Three views:
 * - form: pick a date/slot/type (video or phone only) and request the booking;
 * - confirmation: the scheduled appointment summary with reschedule/cancel;
 * - reschedule: pick a new slot and propose it to the vendor.
 * The active booking (from the account's invited bookings) decides between
 * form and confirmation; reschedule is entered explicitly.
 */
const BookVisitDrawer: React.FC<BookVisitDrawerProps> = ({
  quoteId,
  vendorName,
  quoteExpiresAt,
  onClose,
}) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("estimator");
  const [{ data: meData }] = useMeQuery();

  const { loading, availabilityConfigured, availability, busyBookings } =
    useQuoteVendorAvailability(quoteId);
  const { request, requesting } = useRequestBooking();
  const { booking, loading: bookingLoading } = useQuoteBooking(quoteId);
  const { respond, responding } = useBookingResponse();

  // "reschedule" is an explicit override; otherwise the active booking
  // decides between the confirmation and the booking form.
  const [rescheduling, setRescheduling] = useState(false);
  const view = booking
    ? rescheduling
      ? "reschedule"
      : "confirmation"
    : "form";

  const today = moment().format("YYYY-MM-DD");
  const [type, setType] = useState<BookingType>("video");
  const [date, setDate] = useState(today);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [note, setNote] = useState("");

  // Without same-day bookings, today has no slots to offer.
  const earliestDate = availability
    ? earliestBookableDate(availability)
    : today;
  useEffect(() => {
    setDate((current) => (current < earliestDate ? earliestDate : current));
  }, [earliestDate]);

  // The visit has to sit inside both the vendor's booking window and the life
  // of the estimate it is about.
  const expiryMaxDate = quoteMaxDate({ expiresAt: quoteExpiresAt });
  const maxDate = earlierDate(
    availability ? horizonMaxDate(availability.bookingHorizon) : undefined,
    expiryMaxDate,
  );
  const limitedByExpiry = Boolean(maxDate && maxDate === expiryMaxDate);

  // When rescheduling, the booking's own slot shouldn't block the picker.
  const reschedulableBusy = useMemo(
    () =>
      booking
        ? busyBookings.filter(
            (b) =>
              !(
                b.date === booking.date &&
                b.from === booking.fromTime &&
                b.to === booking.toTime
              ),
          )
        : busyBookings,
    [busyBookings, booking],
  );

  const handleConfirm = async () => {
    if (!slot) {
      return;
    }
    const result = await request({
      quoteId,
      type,
      date,
      fromTime: slot.from,
      toTime: slot.to,
      description: note.trim() || undefined,
    });
    if (result.ok) {
      // The graphcache update adds the booking to myInvitedBookings, which
      // flips the view to the confirmation screen.
      showSnack(t("booking.requested"), "success");
      setSlot(null);
    } else {
      showSnack(result.error ?? t("booking.request_failed"), "error");
    }
  };

  const handleReschedule = async () => {
    if (!slot || !booking) {
      return;
    }
    const result = await respond(booking.id, "reschedule", {
      date,
      from: slot.from,
      to: slot.to,
    });
    if (result.ok) {
      showSnack(t("booking.reschedule_requested"), "success");
      setRescheduling(false);
      setSlot(null);
    } else {
      showSnack(result.error ?? t("booking.reschedule_failed"), "error");
    }
  };

  const handleCancel = async () => {
    if (!booking) {
      return;
    }
    const result = await respond(booking.id, "decline");
    if (result.ok) {
      // The withdrawn booking no longer counts as active, so the form returns.
      showSnack(t("booking.cancelled"), "success");
      setRescheduling(false);
    } else {
      showSnack(result.error ?? t("booking.cancel_failed"), "error");
    }
  };

  const selectedTypeIndex = CUSTOMER_REQUEST_TYPES.findIndex(
    (t) => t.id === type,
  );

  const title =
    view === "confirmation"
      ? t("booking.your_appointment")
      : view === "reschedule"
        ? t("booking.reschedule_title", {
            type: booking
              ? (CUSTOMER_TYPE_LABELS[booking.type] ?? t("booking.appointment"))
              : "",
          })
        : t("booking.schedule_with", { vendor: vendorName });

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      // Override the app-wide dark drawer styling: white panel, sized to fit
      // its content.
      PaperProps={{
        sx: {
          background: theme.palette.common.white,
          color: theme.palette.text.primary,
          width: "fit-content",
        },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ScrollContainer height="100%">
          <div
            style={{
              width: CONTENT_WIDTH,
              padding: theme.spacing(3),
              boxSizing: "content-box",
            }}
          >
            {/* Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="h4" style={{ fontWeight: 600 }}>
                {title}
              </Typography>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            {loading || bookingLoading ? (
              <LoadingSpinner />
            ) : view === "confirmation" && booking ? (
              <BookingConfirmation
                booking={booking}
                vendorName={vendorName}
                email={meData?.me?.email ?? ""}
                responding={responding}
                onReschedule={() => {
                  setDate(booking.date >= today ? booking.date : today);
                  setSlot(null);
                  setRescheduling(true);
                }}
                onCancel={handleCancel}
              />
            ) : !availabilityConfigured || !availability ? (
              <Typography variant="body2">
                <RichText
                  t={t}
                  i18nKey="booking.not_enabled"
                  values={{ vendor: vendorName }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
            ) : view === "reschedule" && booking ? (
              <Stack spacing={1.5}>
                <Typography
                  variant="caption"
                  style={{ color: theme.palette.text.secondary }}
                >
                  {t("booking.pick_new", { vendor: vendorName })}
                </Typography>

                <ScheduleFields
                  date={date}
                  slot={slot}
                  availability={availability}
                  busyBookings={reschedulableBusy}
                  maxDate={maxDate}
                  onDateChange={(d) => {
                    setDate(d);
                    setSlot(null);
                  }}
                  onSlotSelect={setSlot}
                />

                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  disabled={!slot}
                  loading={responding}
                  onClick={handleReschedule}
                >
                  {t("booking.request_reschedule")}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={responding}
                  onClick={() => setRescheduling(false)}
                >
                  {t("booking.back")}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Typography
                  variant="caption"
                  style={{ color: theme.palette.text.secondary }}
                >
                  {t("booking.select_time")}
                </Typography>

                {/* Without this the cut-off date reads as an arbitrary
                    greyed-out calendar. */}
                {limitedByExpiry && (
                  <Typography
                    variant="caption"
                    style={{ color: theme.palette.text.secondary }}
                  >
                    {t("booking.dates_stop", {
                      date: moment(maxDate).format("MMM D"),
                    })}
                  </Typography>
                )}

                <ScheduleFields
                  date={date}
                  slot={slot}
                  availability={availability}
                  busyBookings={busyBookings}
                  maxDate={maxDate}
                  onDateChange={(d) => {
                    setDate(d);
                    setSlot(null);
                  }}
                  onSlotSelect={setSlot}
                />

                {/* Meeting details */}
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {t("booking.meeting_details")}
                </Typography>
                <AutoComplete
                  options={CUSTOMER_REQUEST_TYPES.map((t, i) => ({
                    id: i,
                    label: t.label,
                  }))}
                  value={selectedTypeIndex >= 0 ? selectedTypeIndex : null}
                  onChange={(value) => {
                    if (value) {
                      setType(CUSTOMER_REQUEST_TYPES[value.id].id);
                    }
                  }}
                />

                {/* Optional note */}
                <TextArea
                  placeHolder={t("booking.note_placeholder")}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />

                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  disabled={!slot}
                  loading={requesting}
                  onClick={handleConfirm}
                >
                  {t("booking.confirm")}
                </Button>
              </Stack>
            )}

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mt: 2 }}
            >
              <LockOutlinedIcon
                sx={{ fontSize: 14, color: theme.palette.text.secondary }}
              />
              <Typography
                variant="caption"
                style={{ color: theme.palette.text.secondary }}
              >
                {t("booking.secure", { vendor: vendorName })}
              </Typography>
            </Stack>
          </div>
        </ScrollContainer>
      </LocalizationProvider>
    </Drawer>
  );
};

export default BookVisitDrawer;
